import React from 'react';
import { Box, Typography, Link, IconButton, Tooltip, SvgIcon } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import MapIcon from '@mui/icons-material/Map';
import { businessInfo, devInfo } from '../config/info';

function ThreadsIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z" />
    </SvgIcon>
  );
}

const SOCIAL_CONFIG = [
  {
    key: 'instagram',
    label: 'Instagram',
    Icon: InstagramIcon,
    hoverBg: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
  },
  {
    key: 'threads',
    label: 'Threads',
    Icon: ThreadsIcon,
    hoverBg: '#000000',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    Icon: FacebookIcon,
    hoverBg: '#1877F2',
  },
  {
    key: 'googleMaps',
    label: 'Google Maps',
    Icon: MapIcon,
    hoverBg: 'green',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    Icon: YouTubeIcon,
    hoverBg: '#FF0000',
  },
];

const Footer = () => (
  <Box
    id="contact"
    component="footer"
    sx={{
      bgcolor: 'var(--color-charcoal)',
      color: 'var(--color-cream)',
      py: 6,
      px: 3,
    }}
  >
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          gap: 4,
          pb: 4,
          borderBottom: '1px solid rgba(250,248,245,0.15)',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: '1.75rem',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {businessInfo.name}
          </Typography>
          <Typography sx={{ color: 'rgba(250,248,245,0.8)', fontSize: '0.95rem' }}>
            {businessInfo.tagline}
          </Typography>
          <Typography sx={{ color: 'rgba(250,248,245,0.8)', fontSize: '0.95rem', mt: 0.5 }}>
            {businessInfo.address}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '0.9rem' }}>Contact</Typography>
          {businessInfo.phones.map((phone) => {
            const digits = phone.replace(/\D/g, '');
            const isWhatsApp = digits.length === 10;
            const href = isWhatsApp ? `https://wa.me/91${digits}` : `tel:${phone}`;
            return (
              <Link
                key={phone}
                href={href}
                target={isWhatsApp ? '_blank' : undefined}
                rel={isWhatsApp ? 'noopener noreferrer' : undefined}
                sx={{
                  color: 'var(--color-cream)',
                  textDecoration: 'none',
                  display: 'block',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {phone.length === 10 ? `+91 ${phone}` : phone}
              </Link>
            );
          })}
          {businessInfo.email && (
            <Link
              href={`mailto:${businessInfo.email}`}
              sx={{
                color: 'var(--color-cream)',
                textDecoration: 'none',
                display: 'block',
                mt: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {businessInfo.email}
            </Link>
          )}
        </Box>
      </Box>

      {(() => {
        const social = businessInfo.socialLinks || {};
        const items = SOCIAL_CONFIG.filter(({ key }) => social[key]?.trim());
        if (items.length === 0) return null;
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1.5,
              pt: 3,
              pb: 1,
            }}
          >
            {items.map(({ key, label, Icon, hoverBg }) => (
              <Tooltip key={key} title={label} arrow>
                <IconButton
                  component="a"
                  href={social[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  size="large"
                  sx={{
                    color: 'rgba(250,248,245,0.9)',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(250,248,245,0.12)',
                    width: 52,
                    height: 52,
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      color: '#fff',
                      bgcolor: hoverBg,
                      borderColor: 'transparent',
                      transform: 'translateY(-4px) scale(1.06)',
                      boxShadow:
                        key === 'instagram'
                          ? '0 12px 28px rgba(188, 24, 136, 0.45)'
                          : key === 'threads'
                            ? '0 12px 28px rgba(0, 0, 0, 0.45)'
                            : '0 12px 28px rgba(0, 0, 0, 0.35)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 26,
                    },
                  }}
                >
                  <Icon />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        );
      })()}

      <Box
        sx={{
          pt: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(250,248,245,0.6)' }}>
          Developed by <strong style={{ color: 'rgba(250,248,245,0.9)' }}>{devInfo.name}</strong>
          {' · '}{devInfo.location}
          {' · '}
          <Link
            href={`https://wa.me/${devInfo.whatsapp || '918778927733'}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'rgba(250,248,245,0.8)',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {devInfo.contact}
          </Link>
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default Footer;
